import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { CheckCircle2, AlertCircle, Loader2, Instagram, ArrowRight } from 'lucide-react';

export const MetaCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Vinculando tus cuentas de Facebook e Instagram...');
  const [accountsCount, setAccountsCount] = useState(0);

  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error') || searchParams.get('error_description');

    if (error) {
      setStatus('error');
      setMessage(`Acceso cancelado o denegado por Meta: ${error}`);
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No se encontró el código de autorización en la URL.');
      return;
    }

    const exchangeCode = async () => {
      try {
        const redirectUri = window.location.origin + '/auth/meta/callback';
        const res = await api.handleMetaCallback(code, redirectUri);

        setStatus('success');
        setAccountsCount(res.accounts_count || 0);
        setMessage(
          `¡Meta conectado exitosamente! Se vincularon ${res.accounts_count} páginas y cuentas de Instagram.`
        );

        setTimeout(() => {
          navigate('/clientes');
        }, 2500);
      } catch (err: any) {
        console.error('Error al autorizar Meta:', err);
        setStatus('error');
        setMessage(err.message || 'Error al intercambiar credenciales con Meta.');
      }
    };

    exchangeCode();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center transition-all">
        {/* Ícono */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center bg-gradient-to-tr from-amber-500 via-rose-500 to-purple-600 text-white shadow-lg shadow-pink-100">
          {status === 'loading' && <Loader2 className="w-8 h-8 animate-spin text-white" />}
          {status === 'success' && <CheckCircle2 className="w-8 h-8 text-white animate-bounce" />}
          {status === 'error' && <AlertCircle className="w-8 h-8 text-white" />}
        </div>

        {/* Título */}
        <h2 className="text-xl font-bold text-slate-900 mb-2">
          {status === 'loading' && 'Conectando con Meta'}
          {status === 'success' && '¡Cuentas Vinculadas con Éxito!'}
          {status === 'error' && 'No se pudo conectar con Meta'}
        </h2>

        {/* Mensaje */}
        <p className="text-slate-600 text-sm mb-6 leading-relaxed">
          {status === 'loading' &&
            'Estamos obteniendo las páginas de Facebook y perfiles de Instagram comerciales a los que tienes acceso.'}
          {status === 'success' && (
            <span>
              {message} Ahora la app podrá consultar el alcance, seguidores y engagement de tus
              clientes automáticamente.
              {accountsCount > 0 && (
                <span className="block mt-2 font-semibold text-pink-600">
                  {accountsCount} {accountsCount === 1 ? 'cuenta lista' : 'cuentas listas'} para sincronizar métricas.
                </span>
              )}
            </span>
          )}
          {status === 'error' && message}
        </p>

        {/* Botón de acción */}
        {status === 'success' && (
          <div className="pt-2">
            <button
              onClick={() => navigate('/clientes')}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium bg-gradient-to-r from-pink-600 to-indigo-600 hover:from-pink-700 hover:to-indigo-700 shadow-md shadow-pink-200 transition-all text-sm"
            >
              Ir a Mis Clientes
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-3 pt-2">
            <button
              onClick={() => navigate('/clientes')}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-slate-700 font-medium bg-slate-100 hover:bg-slate-200 transition-all text-sm"
            >
              Regresar
            </button>
          </div>
        )}

        {/* Pie */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-400">
          <Instagram className="w-3.5 h-3.5 text-pink-500" />
          <span>Instagram Graph API & Meta Business Suite</span>
        </div>
      </div>
    </div>
  );
};
